import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { createRecord } from '../../services/recordStorageService';
import { logRecordCreated } from '../../services/activityLogService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { StandardButton } from '../standard';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

export interface CreditBlockQuickAddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (clientId: string) => void;
}

export const CreditBlockQuickAddClientModal: React.FC<CreditBlockQuickAddClientModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { userId, userName } = useCurrentUser();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [accountName, setAccountName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ownerName, setOwnerName] = useState('');

  const resetForm = () => {
    setAccountName('');
    setEmail('');
    setPhone('');
    setOwnerName('');
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!accountName.trim()) throw new Error('Account name is required.');
      const objectType = `Client${CUSTOM_SUFFIX}`;
      const payload: Record<string, unknown> = {
        Name__c: accountName.trim(),
        AccountName__c: accountName.trim(),
        name: accountName.trim(),
        accountName: accountName.trim(),
        SubscriptionStatus__c: 'active',
        subscriptionStatus: 'active',
      };
      if (email.trim()) {
        payload.PersonEmail__c = email.trim();
        payload.Email__c = email.trim();
        payload.email = email.trim();
      }
      if (phone.trim()) {
        payload.Phone__c = phone.trim();
        payload.phone = phone.trim();
      }
      if (ownerName.trim()) {
        payload.Owner__c = ownerName.trim();
        payload.owner = ownerName.trim();
        payload.AccountOwner__c = ownerName.trim();
      }
      payload.Products__c = ['Credit Block'];
      payload.products = ['Credit Block'];
      return createRecord(objectType, payload);
    },
    onSuccess: (rec) => {
      const clientId = String(rec.id);
      logRecordCreated(clientId, `Client${CUSTOM_SUFFIX}`, userId, userName, {
        quickAddClient: true,
        source: 'CreditBlock QuickAdd',
      });
      success('Client created', `${accountName.trim()} is now available in Credit Block`);
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords'] });
      resetForm();
      onClose();
      onCreated?.(clientId);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Could not create client.';
      showError('Create failed', msg);
    },
  });

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Add Client</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Bridge for UC5: create a Client record in Gobodo immediately so the account is actionable
            before the nightly Salesforce sync.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Account name <span className="text-red-500">*</span>
            </label>
            <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)} className={inputClass} placeholder="Client / company name" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="client@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account owner</label>
            <input type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} className={inputClass} placeholder="Assigned rep name" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
          <StandardButton variant="ghost" type="button" onClick={onClose} disabled={createMutation.isPending}>Cancel</StandardButton>
          <StandardButton variant="primary" type="button" onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !accountName.trim()}>
            {createMutation.isPending ? 'Creating...' : 'Create Client'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
